/**
 * /learn/courses/:courseId/sections/:sectionId/modules — modules-in-section
 * grid. Matches Figma 9173:279036.
 *
 * Header: "SECTION N:" eyebrow in purple + section title in dark.
 * Centered progress bar. Modules render inside the gradient PreviewTileGrid;
 * locked tiles use "Finish The Previous Content To Access This!" copy
 * (note: "Content" — the smaller scope inside a section).
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useImageUrl } from '../../services/uploads';
import PreviewTile from '../../components/programs/preview/PreviewTile';
import PreviewTileGrid from '../../components/programs/preview/PreviewTileGrid';
import { PreviewList, default as PreviewListItem } from '../../components/programs/preview/PreviewList';
import { BackPill } from '../../components/programs/preview/PreviewWelcomeHero';
import LearnerCourseProgressBar from '../../components/learner/LearnerCourseProgressBar';
import StickyProgressRow from '../../components/learner/StickyProgressRow';
import { getEnrolmentAvailability, isEnrolmentLocked } from '../../utils/enrolmentAvailability';
import type { components } from '../../types/learnerApi';

type ContentModule = components['schemas']['ContentModule'];
type EvaluationModule = components['schemas']['EvaluationModule'];
type FeedbackModule = components['schemas']['FeedbackModule'];
type AnyModule = ContentModule | EvaluationModule | FeedbackModule;

function moduleStatus(m: AnyModule): 'not_started' | 'in_progress' | 'completed' | undefined {
  return (m as ContentModule).progress?.status as
    | 'not_started'
    | 'in_progress'
    | 'completed'
    | undefined;
}

const CourseSectionModulesPage: React.FC = () => {
  const { assignmentId, sectionId } = useParams<{
    assignmentId?: string;
    sectionId: string;
  }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const routes = useCourseRoutes(id);
  const { data: course, isLoading, isError } = useCourse(id);

  // Date-window gate: bounce deep-links back to the detail page when the
  // enrolment hasn't opened yet or has already ended.
  useEffect(() => {
    if (!course) return;
    const availability = getEnrolmentAvailability(course.assignment, {
      completed: course.progress?.bucket === 'completed',
    });
    if (isEnrolmentLocked(availability)) {
      navigate(routes.base, { replace: true });
    }
  }, [course, navigate, routes.base]);

  const sortedSections = useMemo(() => {
    return (course?.sections ?? [])
      .slice()
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, [course]);

  const sectionIdx = sortedSections.findIndex((s) => s.id === sectionId);
  const section = sectionIdx >= 0 ? sortedSections[sectionIdx] : null;

  const sortedModules = useMemo(() => {
    return ((section?.modules ?? []) as AnyModule[])
      .slice()
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  }, [section]);

  // Cursor inside this section: index of in_progress, else first not_started.
  // Earlier sections must be done before this section unlocks at all.
  const cursorIdx = useMemo(() => {
    for (let i = 0; i < sectionIdx; i++) {
      const earlier = (sortedSections[i].modules ?? []) as AnyModule[];
      if (earlier.some((m) => moduleStatus(m) !== 'completed')) {
        return -1;
      }
    }
    let firstNotStarted = -1;
    for (let i = 0; i < sortedModules.length; i++) {
      const status = moduleStatus(sortedModules[i]);
      if (status === 'in_progress') return i;
      if (firstNotStarted < 0 && (!status || status === 'not_started')) {
        firstNotStarted = i;
      }
    }
    return firstNotStarted;
  }, [sectionIdx, sortedSections, sortedModules]);

  if (isLoading) return <ModulesPageSkeleton />;

  if (isError || !course) {
    return (
      <div className="w-[95%] sm:w-[90%] mx-auto py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't load this course.</p>
        <Link to={routes.coursesIndex} className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to My Courses
        </Link>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="w-[95%] sm:w-[90%] mx-auto py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't find that section.</p>
        <Link
          to={`${routes.base}/sections`}
          className="text-sm font-bold text-primary-500 hover:underline"
        >
          ← Back to sections
        </Link>
      </div>
    );
  }

  const courseCompleted = course.progress?.bucket === 'completed';

  return (
    <motion.div
      {...slideUp}
      className="w-[95%] sm:w-[90%] mx-auto py-6 sm:py-10 space-y-6 sm:space-y-8"
    >
      {/* Header: "SECTION N:" eyebrow + section title */}
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight px-1">
        <span className="text-primary-500 uppercase tracking-tight">
          Section {sectionIdx + 1}:
        </span>{' '}
        <span className="text-text-primary">
          {section.title ?? `Section ${sectionIdx + 1}`}
        </span>
      </h1>

      {/* Back pill + progress bar on the same row — pinned while scrolling. */}
      <StickyProgressRow>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 sm:gap-x-8 px-1">
          <BackPill
            label="Go back"
            onClick={() => navigate(`${routes.base}/sections`)}
          />
          {course.progress && (
            <div className="flex-1 min-w-[220px]">
              <LearnerCourseProgressBar
                percent={course.progress.progress_percent ?? 0}
                modulesCompleted={course.progress.modules_completed ?? 0}
                modulesTotal={course.progress.modules_total ?? 0}
              />
            </div>
          )}
        </div>
      </StickyProgressRow>

      {/* Modules grid in gradient frame */}
      {sortedModules.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-secondary">
          This section doesn't have any modules yet.
        </div>
      ) : course?.layout === 'list' ? (
        <PreviewList>
          {sortedModules.map((module, i) => (
            <ModuleListCell
              key={module.id}
              courseBase={routes.base}
              sectionId={section.id ?? ''}
              module={module}
              index={i}
              cursorIdx={cursorIdx}
              courseCompleted={courseCompleted}
            />
          ))}
        </PreviewList>
      ) : (
        <PreviewTileGrid maxColumns={4}>
          {sortedModules.map((module, i) => (
            <ModuleTileCell
              key={module.id}
              courseBase={routes.base}
              sectionId={section.id ?? ''}
              module={module}
              index={i}
              cursorIdx={cursorIdx}
              courseCompleted={courseCompleted}
              reduceMotion={!!reduceMotion}
            />
          ))}
        </PreviewTileGrid>
      )}
    </motion.div>
  );
};

export default CourseSectionModulesPage;

interface ModuleTileCellProps {
  courseBase: string;
  sectionId: string;
  module: AnyModule;
  index: number;
  cursorIdx: number;
  courseCompleted: boolean;
  reduceMotion: boolean;
}

function ModuleTileCell({
  courseBase,
  sectionId,
  module,
  index,
  cursorIdx,
  courseCompleted,
  reduceMotion,
}: ModuleTileCellProps) {
  const navigate = useNavigate();
  // Pull the cover from the module's top-level `image_url` (declared on
  // ModuleBase). Resolve the S3 key into a presigned URL for the tile.
  const rawImage = (module as { image_url?: string | null }).image_url ?? null;
  const [coverUrl] = useImageUrl(rawImage);

  const status = moduleStatus(module);
  const state: 'open' | 'locked' | 'finished' = useMemo(() => {
    if (status === 'completed') return 'finished';
    if (courseCompleted) return 'open';
    if (cursorIdx < 0) return 'locked';
    if (index < cursorIdx) return 'finished';
    if (index === cursorIdx) return 'open';
    return 'locked';
  }, [status, courseCompleted, cursorIdx, index]);

  const handleClick = () => {
    if (state === 'locked' || !module.id) return;
    navigate(`${courseBase}/sections/${sectionId}/modules/${module.id}`);
  };

  return (
    <PreviewTile
      imageUrl={coverUrl ?? undefined}
      label={module.title ?? `Module ${index + 1}`}
      state={state}
      // "Finish The Previous Content To Access This!" — Content scope
      // matches the Figma's smaller-than-section locking copy.
      lockedScope="Content"
      entranceDelay={reduceMotion ? 0 : index * 0.05}
      onClick={state === 'locked' ? undefined : handleClick}
    />
  );
}

/** List-layout row for a module — same gating as ModuleTileCell. */
function ModuleListCell({
  courseBase,
  sectionId,
  module,
  index,
  cursorIdx,
  courseCompleted,
}: Omit<ModuleTileCellProps, 'reduceMotion'>) {
  const navigate = useNavigate();
  const rawImage = (module as { image_url?: string | null }).image_url ?? null;
  const status = moduleStatus(module);
  const state: 'open' | 'locked' | 'finished' = useMemo(() => {
    if (status === 'completed') return 'finished';
    if (courseCompleted) return 'open';
    if (cursorIdx < 0) return 'locked';
    if (index < cursorIdx) return 'finished';
    if (index === cursorIdx) return 'open';
    return 'locked';
  }, [status, courseCompleted, cursorIdx, index]);

  return (
    <PreviewListItem
      imageUrl={rawImage}
      kicker={`Content ${index + 1}`}
      index={index}
      title={module.title ?? `Module ${index + 1}`}
      state={state}
      lockedScope="content"
      entranceDelay={index * 0.04}
      onClick={() => {
        if (module.id) navigate(`${courseBase}/sections/${sectionId}/modules/${module.id}`);
      }}
    />
  );
}

function ModulesPageSkeleton() {
  return (
    <div className="w-[95%] sm:w-[90%] mx-auto py-6 sm:py-10 space-y-6 animate-pulse">
      <div className="h-12 w-2/3 rounded bg-text-tertiary/10" />
      <div className="h-[58px] max-w-[420px] mx-auto rounded-full bg-text-tertiary/10" />
      <div className="rounded-[28px] p-3 bg-text-tertiary/5">
        <div className="rounded-[20px] bg-background-primary p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="aspect-[2/3] rounded-2xl bg-text-tertiary/10" />
          ))}
        </div>
      </div>
    </div>
  );
}
